import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container } from '../components/layout/Container';
import { Card } from '../components/common/Card';
import { Spinner } from '../components/common/Spinner';
import { api } from '../config/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export const PaymentCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing your payment...');
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent duplicate verification calls
    if (hasVerified.current) {
      return;
    }

    const verifyPayment = async () => {
      const reference = searchParams.get('reference') || searchParams.get('trxref');
      
      if (!reference) {
        setStatus('error');
        setMessage('Invalid payment reference');
        return;
      }

      hasVerified.current = true;

      try {
        const response = await api.post('/webhooks/paystack/verify', { reference });
        
        console.log('Verification response:', response.data);
        
        if (response.data.success) {
          setStatus('success');

          if (isAuthenticated) {
            const msg = response.data.ticketsPurchased 
              ? 'Payment successful! Your tickets have been purchased.' 
              : 'Payment successful! Your wallet has been credited.';
            setMessage(msg + ' Redirecting...');
            toast.success(msg);
            
            // Redirect authenticated users to their tickets or wallet
            setTimeout(() => {
              if (response.data.ticketsPurchased) {
                navigate('/my-tickets');
              } else {
                navigate('/wallet');
              }
            }, 2000);
          } else {
            // Guest user - show success and redirect to home
            const msg = response.data.ticketsPurchased
              ? 'Payment successful! Your tickets have been purchased. Check your email to set up your account password.'
              : 'Payment successful! Your wallet has been credited. Check your email to set up your account password.';
            setMessage(msg);
            toast.success('Payment successful! Check your email to access your account.');
            
            setTimeout(() => {
              navigate('/');
            }, 4000);
          }
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Payment verification failed');
          toast.error(response.data.message || 'Payment verification failed');
        }
      } catch (error: any) {
        console.error('Payment verification error:', error);
        console.error('Error response:', error.response);
        setStatus('error');
        setMessage(error.response?.data?.message || 'Payment verification failed');
        toast.error('Payment verification failed');
      }
    };

    verifyPayment();
  }, []); // Empty dependency array - only run once on mount

  return (
    <Container className="py-12">
      <Card className="max-w-md mx-auto text-center">
        {status === 'processing' && (
          <>
            <Spinner size="lg" className="mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Processing Payment</h2>
            <p className="text-gray-600 dark:text-gray-400">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-green-600 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 dark:text-gray-400">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Payment Failed</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{message}</p>
            <button
              onClick={() => navigate(isAuthenticated ? '/wallet' : '/')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {isAuthenticated ? 'Return to Wallet' : 'Return to Home'}
            </button>
          </>
        )}
      </Card>
    </Container>
  );
};
